import { getApiBaseUrl } from './api';
import { authService } from './authService';

// Single shared SSE connection — scoped to either the currently selected
// store (Lead Manager) or an entire enterprise (GI dashboard, which shows
// leads across every store in it) — for real-time lead/conversation events.
// StrictMode remounts and any number of components (App, GIDashboard,
// ChatBox) can subscribe to the same stream without each opening their own
// connection.
let eventSource = null;
let currentScopeKey = null; // e.g. "store:12" or "enterprise:5"
const subscribers = new Set();

const notifySubscribers = (event) => {
  subscribers.forEach((callback) => {
    try {
      callback(event);
    } catch (err) {
      console.error('leadEventBus subscriber threw:', err);
    }
  });
};

const openConnection = (scopeKey, queryParam, scopeId) => {
  // Already connected to this exact scope — nothing to do. This makes
  // connect()/connectEnterprise() safe to call on every render/effect run
  // without tearing down and reopening the stream unnecessarily.
  if (eventSource && currentScopeKey === scopeKey) {
    return;
  }

  if (eventSource) {
    eventSource.close();
    eventSource = null;
  }

  currentScopeKey = scopeKey;

  if (!scopeId) {
    return;
  }
  const token = authService.getToken();
  const url = `${getApiBaseUrl()}/events?${queryParam}=${scopeId}${token ? `&token=${encodeURIComponent(token)}` : ''}`;

  const es = new EventSource(url);
  eventSource = es;

  es.addEventListener('lead-event', (evt) => {
    try {
      const data = JSON.parse(evt.data);
      notifySubscribers(data);
    } catch (err) {
      console.error('Failed to parse lead event payload:', err);
    }
  });

  es.addEventListener('connected', () => {
    console.log('📡 Lead event stream connected for', scopeKey);
  });

  // The browser's EventSource retries automatically on a dropped connection
  // (default backoff, or server-set retry interval) — nothing to do here but
  // log. This, plus the server's periodic heartbeat, is what lets the
  // frontend truly stay idle (no polling) while remaining resilient to a
  // network blip or an ALB/proxy timing the connection out.
  es.onerror = (err) => {
    console.warn('Lead event stream error (browser will auto-reconnect):', err);
  };
};

export const leadEventBus = {
  // Open (or, if the store changed, reopen) the SSE connection for this store.
  // Safe to call repeatedly with the same storeIdFk.
  connect(storeIdFk) {
    openConnection(storeIdFk ? `store:${storeIdFk}` : null, 'storeIdFk', storeIdFk);
  },

  // Open (or, if the enterprise changed, reopen) the SSE connection for this
  // enterprise — used by the GI dashboard, which shows leads across every
  // store in the enterprise rather than a single selected store.
  connectEnterprise(enterpriseId) {
    openConnection(enterpriseId ? `enterprise:${enterpriseId}` : null, 'enterpriseIdFk', enterpriseId);
  },

  // Subscribe to lead events ({ type, leadId, storeIdFk, enterpriseIdFk, direction, lastActivity }).
  // Returns an unsubscribe function.
  subscribe(callback) {
    subscribers.add(callback);
    return () => {
      subscribers.delete(callback);
    };
  },

  // Tear down the connection entirely (e.g. on logout, or leaving the dashboard route).
  disconnect() {
    if (eventSource) {
      eventSource.close();
      eventSource = null;
    }
    currentScopeKey = null;
  },
};

export default leadEventBus;
